import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity } from '@redux/entities'

import api from '@functions/api'

import type { OrderBundleItemModel } from '@models/types'

import type { AppDispatch } from '@redux/store'
import type {
  Action, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/orderBundleItem/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/orderBundleItem/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/orderBundleItem/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/orderBundleItem/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/orderBundleItem/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/orderBundleItem/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/orderBundleItem/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/orderBundleItem/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/orderBundleItem/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/orderBundleItem/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/orderBundleItem/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/orderBundleItem/DELETE_FAILURE'

export type OrderBundleItemModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: Errors,
  loaded: boolean,
  loadedForKeys: string[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: OrderBundleItemModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}): Action {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
  }
}

export function fetchSuccess(): Action {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []): Action {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(): Action {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(): Action {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []): Action {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(): Action {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(): Action {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []): Action {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(): Action {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(): Action {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []): Action {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadOrderBundleItems(options: EntityOptions = {}) {
  const { entityKey } = options || {}

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().orderBundleItems.loadedForKeys]
    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/order_bundle_items.json', options)
      .then(({ data }: { data: OrderBundleItemModel[] }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_BUNDLE_ITEM_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createOrderBundleItem(orderBundleItem: OrderBundleItemModel, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      order_bundle_item: orderBundleItem,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/order_bundle_items.json', options, config)
      .then(({ data }: { data: OrderBundleItemModel }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_BUNDLE_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateOrderBundleItem(orderBundleItem: Partial<OrderBundleItemModel>, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      order_bundle_item: orderBundleItem,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/order_bundle_items/${orderBundleItem.id}.json`, options, config)
      .then(({ data }: { data: OrderBundleItemModel }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_BUNDLE_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteOrderBundleItem(orderBundleItem: OrderBundleItemModel) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/order_bundle_items/${orderBundleItem.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_BUNDLE_ITEM)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })
    return promise
  }
}

// Reducer
export default function reducer(
  state: OrderBundleItemModuleState = initialState,
  action: Action = { type: '' },
): OrderBundleItemModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
